import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Tag, Loader2, Trash2, Pencil, Check, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  code: '',
  type: 'percent',
  value: 10,
  min_subtotal: 0,
  max_uses: '',
  expires_at: '',
  description: '',
};

export const AdminVouchersPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const r = await axios.get(`${API}/admin/vouchers`, { headers: { Authorization: `Bearer ${token}` } });
      setVouchers(r.data || []);
    } catch (_) { /* silent */ } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (token) fetchAll(); }, [token]);

  const onSave = async () => {
    if (!form.code || !form.type || !form.value) {
      toast.error(T('Code, type et valeur sont requis', 'Code, type and value are required'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: parseFloat(form.value),
        min_subtotal: parseFloat(form.min_subtotal) || 0,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at || null,
        description: form.description || null,
      };
      await axios.post(`${API}/admin/vouchers`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(T('Code créé', 'Voucher created'));
      setDialogOpen(false);
      setForm(initialForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(
        `${API}/admin/vouchers/${v.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
    } catch (err) { toast.error(T('Erreur', 'Error')); }
  };

  const deleteVoucher = async (v) => {
    if (!confirm(T(`Supprimer le code ${v.code} ?`, `Delete voucher ${v.code}?`))) return;
    try {
      await axios.delete(`${API}/admin/vouchers/${v.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(T('Code supprimé', 'Voucher deleted'));
      fetchAll();
    } catch (err) { toast.error(T('Erreur', 'Error')); }
  };

  return (
    <div className="container-main py-8" data-testid="admin-vouchers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#0F4C5C] tracking-tight">{T('Codes promo', 'Vouchers')}</h1>
          <p className="text-muted-foreground mt-1">
            {T(`${vouchers.length} code(s) — gérez vos campagnes marketing`, `${vouchers.length} code(s) — manage your marketing campaigns`)}
          </p>
        </div>
        <Button onClick={() => { setForm(initialForm); setDialogOpen(true); }} className="btn-primary" data-testid="new-voucher-btn">
          <Plus className="w-4 h-4 mr-2" /> {T('Nouveau code', 'New voucher')}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
        ) : vouchers.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground">
            <Tag className="w-12 h-12 mx-auto text-[#0F4C5C]/30 mb-3" />
            <p>{T('Aucun code promo créé pour l\'instant.', 'No vouchers created yet.')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FBF8F3] text-[#0F4C5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-4 font-semibold">{T('Code', 'Code')}</th>
                  <th className="text-left p-4 font-semibold">{T('Réduction', 'Discount')}</th>
                  <th className="text-left p-4 font-semibold">{T('Min. achat', 'Min. cart')}</th>
                  <th className="text-left p-4 font-semibold">{T('Utilisations', 'Uses')}</th>
                  <th className="text-left p-4 font-semibold">{T('Expire', 'Expires')}</th>
                  <th className="text-left p-4 font-semibold">{T('Statut', 'Status')}</th>
                  <th className="text-right p-4 font-semibold">{T('Actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F4C5C]/5">
                {vouchers.map((v) => (
                  <tr key={v.id} className="hover:bg-[#FBF8F3]/50" data-testid={`voucher-row-${v.code}`}>
                    <td className="p-4">
                      <p className="font-mono font-bold text-[#0F4C5C]">{v.code}</p>
                      {v.description && <p className="text-xs text-muted-foreground">{v.description}</p>}
                    </td>
                    <td className="p-4 font-semibold text-[#0F4C5C]">
                      {v.type === 'percent' ? `${v.value}%` : `${v.value.toFixed(2)} €`}
                    </td>
                    <td className="p-4">{v.min_subtotal > 0 ? `${v.min_subtotal.toFixed(2)} €` : '—'}</td>
                    <td className="p-4">
                      {v.usage_count}
                      {v.max_uses != null && ` / ${v.max_uses}`}
                    </td>
                    <td className="p-4">{v.expires_at ? new Date(v.expires_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : '—'}</td>
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => toggleStatus(v)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          v.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {v.status === 'active' ? T('Actif', 'Active') : T('Désactivé', 'Disabled')}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => deleteVoucher(v)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                        aria-label="Delete"
                        data-testid={`delete-voucher-${v.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{T('Créer un code promo', 'Create voucher')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{T('Code (ex: BIENVENUE10)', 'Code (e.g. WELCOME10)')}</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="BIENVENUE10"
                className="mt-1.5 font-mono uppercase tracking-wider"
                data-testid="voucher-form-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{T('Type', 'Type')}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="voucher-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">{T('Pourcentage (%)', 'Percent (%)')}</SelectItem>
                    <SelectItem value="fixed">{T('Montant fixe (€)', 'Fixed amount (€)')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{T('Valeur', 'Value')}</Label>
                <Input
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder={form.type === 'percent' ? '10' : '5.00'}
                  className="mt-1.5"
                  data-testid="voucher-form-value"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{T('Min. d\'achat (€)', 'Min. subtotal (€)')}</Label>
                <Input
                  type="number"
                  value={form.min_subtotal}
                  onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
                  placeholder="30"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>{T('Utilisations max', 'Max uses')}</Label>
                <Input
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder={T('Illimité', 'Unlimited')}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>{T('Date d\'expiration', 'Expiration date')}</Label>
              <Input
                type="date"
                value={form.expires_at ? form.expires_at.slice(0, 10) : ''}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{T('Description (optionnelle)', 'Description (optional)')}</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={T('Ex: Code de bienvenue 10%', 'E.g. Welcome 10% off')}
                className="mt-1.5"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-[#0F4C5C]/70 bg-[#FBF8F3] rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {T(
                  'Le code sera utilisable une seule fois par utilisateur, automatiquement.',
                  'The code can be used once per user, automatically.'
                )}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{T('Annuler', 'Cancel')}</Button>
            <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="voucher-form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {T('Créer', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
